import React from 'react';
import { useLocation } from 'react-router-dom';
import { BottomNav } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { NAV_ITEMS, activeNavFor, isTopLevelPath } from '../../constants/nav.js';

// The mobile shell — what replaces DentistLayout below 768px.
//
// DESIGN-CONCEPT-MOBILE §3: "The shell is bottom navigation + per-screen
// headers. There is no sidebar, no drawer, no shared TopBar." The rail is not
// collapsed here, it is GONE: a 68px mini-rail on a 360px viewport is a
// shrunken workstation, which is the thing the concept exists to reject, and it
// spends 19% of the width on chrome that a thumb cannot comfortably reach
// anyway.
//
// Each screen supplies its own gradient MobileHeader (§3.2). This file owns
// only two things: the single page scroll, and the bar.

/*
 * `position: fixed; inset: 0` is LOAD-BEARING, not a style choice.
 *
 * styles.css sets `#root { display:flex; align-items:center; justify-content:
 * center; min-height:100vh }` over `body { background:#0f1923 }` — the frame the
 * pre-app auth screens are centred in. A flow-positioned mobile shell inherits
 * that and renders as a centred box floating on a dark page. Taking the shell
 * out of flow is what makes the app fill the phone.
 *
 * It is also the shape §6's "one page-level scroll" needs: an
 * `inset:0; overflow:hidden` root with exactly ONE inner scroll region. (It is
 * the same shape the kit's ChatThread documents when it resolves its scroller —
 * a mobile shell is not the document.)
 */
const ROOT = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: C.bg,
  // §8: "The mobile root owns env(safe-area-inset-*)". The TOP inset belongs to
  // MobileHeader (it already pads `calc(1rem + env(safe-area-inset-top))`) and
  // the BOTTOM to BottomNav, so what is left for the root is the notch's left
  // and right — the landscape case, where content would otherwise run under the
  // rounded corners.
  paddingInlineStart: 'env(safe-area-inset-left, 0px)',
  paddingInlineEnd: 'env(safe-area-inset-right, 0px)',
  // No fontFamily: Meshkat is set on html/body in styles.css and inherited.
  // A component-level font declaration is a defect (§2.1).
};

/*
 * The body does NOT scroll — each screen owns exactly ONE scroll region inside
 * it, and that is the deliberate choice §6 forces ("one page-level scroll;
 * nested vertical scroll regions are a defect").
 *
 * A scrolling body here would make two: the screen's own scroller nested in the
 * shell's. It would also make the two rules the concept states about the header
 * impossible to express — §3.2 wants the cockpit's header PINNED (امروز, where
 * the day's numbers must never leave the screen) and a content screen's header
 * to SCROLL AWAY (the record, where content wins the viewport). Which of the two
 * happens is decided by whether a screen puts its MobileHeader inside its
 * scroller, and that decision only exists if the shell is not scrolling for it.
 */
const BODY = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const MobileShell = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();

  const active = activeNavFor(location.pathname);
  // §3.1: the bar renders only on top-level destinations. A patient record and
  // نسخه are full-screen surfaces whose MobileHeader carries the way back —
  // a bottom bar there would offer «بیماران» as an escape from a record the
  // dentist is halfway through charting.
  const showNav = isTopLevelPath(location.pathname);

  const items = React.useMemo(
    () =>
      NAV_ITEMS.map(({ id, labelKey, Icon }) => ({
        key: id,
        label: t(labelKey),
        icon: <Icon size={22} aria-hidden />,
        // No badges: nothing in this app produces an unread count, and a chip
        // that is always absent is one more thing to keep true.
      })),
    [t],
  );

  return (
    <div style={ROOT} data-testid="mobile-shell">
      {/* A <main>, so a screen reader user has a landmark to jump to — the
          same pair of landmarks the desktop rail and body give. */}
      <main aria-label={t('nav.landmark.content')} style={BODY} data-testid="mobile-scroll">
        {children}
      </main>

      {showNav && (
        <BottomNav
          items={items}
          active={active}
          onSelect={(key) => navigate(key)}
          data-testid="dentist-bottom-nav"
          // The kit hardcodes a Persian `aria-label`, and its `...rest` spreads
          // AFTER it, so a caller-supplied one wins. This app ships `en` too.
          aria-label={t('nav.landmark.primary')}
        />
      )}
    </div>
  );
};

export default MobileShell;
